import { useState } from 'react'

import { useSelector } from 'react-redux'

import { toast } from '@webapp/components/Notification'
import {
  makeSelectDomain,
  makeSelectFeatureSwitches,
} from '@webapp/core/stores/global'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { RepoError } from '@webapp/infrastructure/interfaces/repositories/RepoError'
import {
  formatInboxEmailAddressPreview,
  type LegalEntityFormErrors,
  type LegalEntityFormSchema,
} from '@webapp/platform/legal-entities/components/LegalEntityForm'
import { useLegalEntityUniquenessChecks } from '@webapp/platform/legal-entities/hooks/useLegalEntityUniquenessChecks'
import {
  CREATE_LEGAL_ENTITY_STEPS,
  type CreateLegalEntityStep,
} from './CreateLegalEntityStep'
import messages from './messages'
import { useMutateCreateLegalEntity } from '../../models/useMutateCreateLegalEntity'

// Returned with field="handle"; the user stays on review and is prompted to
// fix the inbox name via Edit, which reopens the form with an inline error.
const INBOX_HANDLE_ERROR_CODES = ['INBOX_HANDLE_TAKEN', 'INBOX_HANDLE_INVALID']

const defaultFormValues: LegalEntityFormSchema = {
  name: '',
  country: null,
  externalId: '',
  baseCurrency: null,
  inboxHandle: '',
}

interface UseCreateLegalEntityFormPresenterParams {
  onSuccess?: () => void
}

/**
 * Orchestrates the two-step create wizard (form -> review -> confirm).
 *
 * The form step only collects values; the LegalEntity is persisted on Confirm
 * in the review step.
 */
export const useCreateLegalEntityFormPresenter = ({
  onSuccess,
}: UseCreateLegalEntityFormPresenterParams) => {
  const intl = useProcurifyIntl()
  const { mutateAsync, isLoading } = useMutateCreateLegalEntity()
  const { disable_ap_navigation: disableApNavigation } = useSelector(
    makeSelectFeatureSwitches()
  )
  const showInboxAddress = !disableApNavigation
  const domain = useSelector(makeSelectDomain())

  // Name + External ID are unique per org; surfaced as inline errors on Create.
  // The server re-checks the name on Confirm as the authoritative guard (there
  // is no server-side guard for external ID).
  const { isNameTaken, isExternalIdTaken } = useLegalEntityUniquenessChecks()

  const [step, setStep] = useState<CreateLegalEntityStep>(
    CREATE_LEGAL_ENTITY_STEPS.FORM
  )
  const [formValues, setFormValues] =
    useState<LegalEntityFormSchema>(defaultFormValues)
  const [formErrors, setFormErrors] = useState<LegalEntityFormErrors>({})

  const handleContinue = (values: LegalEntityFormSchema) => {
    setFormValues(values)
    setFormErrors({})
    setStep(CREATE_LEGAL_ENTITY_STEPS.REVIEW)
  }

  const handleEdit = () => {
    setStep(CREATE_LEGAL_ENTITY_STEPS.FORM)
  }

  const handleConfirm = async () => {
    if (!formValues.baseCurrency) {
      setStep(CREATE_LEGAL_ENTITY_STEPS.FORM)

      return
    }

    try {
      await mutateAsync({
        name: formValues.name,
        baseCurrencyId: formValues.baseCurrency.id,
        country: formValues.country?.name,
        externalId: formValues.externalId,
        inboxHandle: showInboxAddress ? formValues.inboxHandle : undefined,
      })

      // Success feedback (toast) and modal close are owned by the hosting
      // FeatureSet via onSuccess — see useLegalEntityListFeatureSet.
      onSuccess?.()
    } catch (err) {
      if (
        err instanceof RepoError &&
        err.code &&
        INBOX_HANDLE_ERROR_CODES.includes(err.code)
      ) {
        const isHandleTaken = err.code === 'INBOX_HANDLE_TAKEN'

        setFormErrors({
          inboxHandle: isHandleTaken
            ? intl.formatMessage(messages.inboxHandleTakenError)
            : err.message,
        })
        toast.error({
          title: intl.formatMessage(messages.errorToastTitle),
          children: isHandleTaken
            ? intl.formatMessage(messages.inboxHandleTakenToast)
            : err.message,
        })

        return
      }

      // The presenter owns the error toast (ADR-10).
      toast.error({
        title: intl.formatMessage(messages.errorToastTitle),
        children: getMessageFromError(err),
      })
    }
  }

  return {
    state: {
      step,
      formValues,
      formErrors,
      isConfirming: isLoading,
      reviewTitle: formValues.name,
      baseCurrencyName: formValues.baseCurrency?.name ?? '',
      reviewCountry: formValues.country?.name ?? '',
      showInboxAddress,
      domain,
      previewInboxEmailAddress: showInboxAddress
        ? formatInboxEmailAddressPreview(domain, formValues.inboxHandle)
        : '',
    },
    methods: {
      handleContinue,
      handleEdit,
      handleConfirm,
      isNameTaken,
      isExternalIdTaken,
    },
  }
}
